import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useOffline } from '../context/OfflineContext';
import { toast } from 'sonner';
import { 
  ChefHat, Clock, Users, Play, Check, RefreshCw, Wifi, WifiOff, Volume2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { BentaWordmark } from '../components/BentaLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function KDSPage() {
  const { token, currentOutlet } = useAuth();
  const { isOnline } = useOffline();
  const selectedOutlet = currentOutlet?.id;
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  
  const fetchOrders = useCallback(async () => {
    if (!selectedOutlet || !token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/kds/orders?outlet_id=${selectedOutlet}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      // Guard against error bodies ({detail: ...}) so the kitchen screen never
      // white-screens on a transient backend error during the 10s auto-refresh.
      setOrders(Array.isArray(data) ? data : []);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to load KDS orders:', err);
    }
    setLoading(false);
  }, [token, selectedOutlet]);
  
  useEffect(() => {
    fetchOrders();
    // Auto-refresh every 10 seconds
    const interval = setInterval(fetchOrders, 10000);
    return () => clearInterval(interval);
  }, [fetchOrders]);
  
  const updateStatus = async (kotId, newStatus) => {
    try {
      await fetch(`${API_URL}/api/kds/orders/${kotId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus })
      });
      
      // Play sound for ready
      if (newStatus === 'ready') {
        playSound();
      }
      
      fetchOrders();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };
  
  const playSound = () => {
    // Create and play a simple beep
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    gainNode.gain.value = 0.3;
    
    oscillator.start();
    setTimeout(() => oscillator.stop(), 200);
  };
  
  // Group orders by status
  const newOrders = orders.filter(o => o.status === 'sent');
  const inProgressOrders = orders.filter(o => o.status === 'in_progress');
  const readyOrders = orders.filter(o => o.status === 'ready');
  
  const formatElapsed = (minutes) => {
    if (!minutes) return '0m';
    if (minutes < 60) return `${minutes}m`;
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  };
  
  const getElapsedColor = (minutes) => {
    if (minutes < 10) return 'text-green-400';
    if (minutes < 20) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <div className="flex flex-col h-screen bg-gray-900" data-testid="kds-page">
      {/* Header */}
      <header className="h-16 bg-gray-800 border-b border-gray-700 flex items-center px-6 justify-between">
        <div className="flex items-center gap-3">
          <BentaWordmark className="text-xl text-white" accentClassName="text-teal-400" />
          <span className="text-gray-600">/</span>
          <ChefHat className="w-7 h-7 text-orange-400" />
          <h1 className="text-xl font-bold text-white">Kitchen Display</h1>
        </div>
        
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-400">
            Last update: {lastRefresh.toLocaleTimeString()}
          </span>
          
          {isOnline ? (
            <span className="flex items-center gap-2 text-green-400 text-sm">
              <Wifi className="w-4 h-4" /> Online
            </span>
          ) : (
            <span className="flex items-center gap-2 text-red-400 text-sm">
              <WifiOff className="w-4 h-4" /> Offline
            </span>
          )}
          
          <Button onClick={fetchOrders} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </header>
      
      {/* KDS Columns */}
      <div className="flex-1 flex p-4 gap-4 overflow-hidden">
        {/* NEW Column */}
        <KDSColumn 
          title="NEW" 
          color="bg-blue-600" 
          orders={newOrders}
          actionLabel="Start"
          actionIcon={<Play className="w-4 h-4" />}
          onAction={(id) => updateStatus(id, 'in_progress')}
          formatElapsed={formatElapsed}
          getElapsedColor={getElapsedColor}
        />
        
        {/* IN PROGRESS Column */}
        <KDSColumn 
          title="IN PROGRESS" 
          color="bg-orange-600" 
          orders={inProgressOrders}
          actionLabel="Ready"
          actionIcon={<Check className="w-4 h-4" />}
          onAction={(id) => updateStatus(id, 'ready')}
          formatElapsed={formatElapsed}
          getElapsedColor={getElapsedColor}
        />
        
        {/* READY Column */}
        <KDSColumn 
          title="READY" 
          color="bg-green-600" 
          orders={readyOrders}
          actionLabel="Served"
          actionIcon={<Check className="w-4 h-4" />}
          onAction={(id) => updateStatus(id, 'served')}
          formatElapsed={formatElapsed}
          getElapsedColor={getElapsedColor}
          showServeButton
        />
      </div>
      
      {/* Footer Stats */}
      <footer className="h-14 bg-gray-800 border-t border-gray-700 flex items-center px-6 justify-between">
        <div className="flex gap-6">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-600" />
            <span className="text-gray-300">New: {newOrders.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-orange-600" />
            <span className="text-gray-300">In Progress: {inProgressOrders.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-600" />
            <span className="text-gray-300">Ready: {readyOrders.length}</span>
          </div>
        </div>
        
        <Button variant="ghost" size="sm" onClick={playSound}>
          <Volume2 className="w-4 h-4 mr-2" /> Test Sound
        </Button>
      </footer>
    </div>
  );
}

function KDSColumn({ title, color, orders, actionLabel, actionIcon, onAction, formatElapsed, getElapsedColor, showServeButton }) {
  return (
    <div className="flex-1 flex flex-col bg-gray-800/50 rounded-lg overflow-hidden min-w-[300px]">
      {/* Column Header */}
      <div className={`h-12 ${color} flex items-center justify-between px-4`}>
        <span className="font-bold text-white">{title}</span>
        <span className="bg-white/20 px-2 py-0.5 rounded text-white text-sm font-medium">
          {orders.length}
        </span>
      </div>
      
      {/* Orders List */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {orders.map(order => (
          <div 
            key={order.id} 
            className="bg-gray-900 rounded-lg border border-gray-700 overflow-hidden"
            data-testid={`kot-${order.kot_number}`}
          >
            {/* Order Header */}
            <div className="bg-gray-800 px-4 py-2 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <span className="font-bold text-white text-lg">{order.kot_number}</span>
                {order.table_name && (
                  <span className="bg-blue-600 text-white px-2 py-0.5 rounded text-sm font-medium">
                    {order.table_name}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1 text-sm text-gray-400">
                  <Users className="w-4 h-4" /> {order.guest_count}
                </span>
                <span className={`flex items-center gap-1 text-sm font-medium ${getElapsedColor(order.elapsed_minutes)}`}>
                  <Clock className="w-4 h-4" /> {formatElapsed(order.elapsed_minutes)}
                </span>
              </div>
            </div>
            
            {/* Order Items */}
            <div className="p-4 space-y-2">
              {order.items?.map((item, idx) => (
                <div key={idx} className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="bg-teal-600 text-white px-2 py-0.5 rounded text-sm font-bold min-w-[2rem] text-center">
                        {item.quantity}x
                      </span>
                      <span className="text-white font-medium">{item.name}</span>
                    </div>
                    {item.modifiers?.length > 0 && (
                      <div className="ml-12 text-sm text-yellow-400">
                        {item.modifiers.join(', ')}
                      </div>
                    )}
                    {item.notes && (
                      <div className="ml-12 text-sm text-orange-400 italic">
                        Note: {item.notes}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {order.notes && (
                <div className="mt-3 p-2 bg-orange-900/30 rounded border border-orange-700 text-orange-300 text-sm">
                  Order Note: {order.notes}
                </div>
              )}
            </div>
            
            {/* Action Button */}
            <div className="px-4 pb-4">
              <Button 
                className={`w-full h-12 text-lg font-bold ${
                  showServeButton ? 'bg-green-600 hover:bg-green-700' : 'bg-teal-600 hover:bg-teal-700'
                }`}
                onClick={() => onAction(order.id)}
                data-testid={`action-${order.kot_number}`}
              >
                {actionIcon}
                <span className="ml-2">{actionLabel}</span>
              </Button>
            </div>
          </div>
        ))}
        
        {orders.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No orders
          </div>
        )}
      </div>
    </div>
  );
}
